import { supabase } from './supabase'

// ─── Types ───────────────────────────────────────────────────

export interface CsvRow {
  category_name: string
  item_name: string
  price: number
  station_code: string
  description?: string
  option_name?: string
  option_price?: number
}

export interface InvalidRow {
  row: number
  data: Record<string, string>
  errors: string[]
}

export interface ValidationResult {
  validRows: CsvRow[]
  invalidRows: InvalidRow[]
}

export interface StructuredCategory {
  name: string
  items: StructuredItem[]
}

export interface StructuredItem {
  name: string
  description: string
  price: number
  station_code: string
  options: StructuredOption[]
}

export interface StructuredOption {
  name: string
  price: number
}

export interface StructuredMenu {
  categories: StructuredCategory[]
  totalCategories: number
  totalItems: number
  totalOptions: number
}

export interface ImportResult {
  success: boolean
  categoriesCreated: number
  itemsCreated: number
  optionsCreated: number
  error?: string
}

// ─── CSV Parsing ─────────────────────────────────────────────

function parseLine(line: string): string[] {
  const result: string[] = []
  let current = ''
  let inQuotes = false

  for (let i = 0; i < line.length; i++) {
    const char = line[i]
    if (char === '"') {
      if (inQuotes && line[i + 1] === '"') {
        current += '"'
        i++
      } else {
        inQuotes = !inQuotes
      }
    } else if (char === ',' && !inQuotes) {
      result.push(current.trim())
      current = ''
    } else {
      current += char
    }
  }
  result.push(current.trim())
  return result
}

export function parseCsv(text: string): Record<string, string>[] {
  const lines = text
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter((l) => l.length > 0)

  if (lines.length < 2) return []

  const headers = parseLine(lines[0]).map((h) =>
    h
      .toLowerCase()
      .replace(/\s+/g, '_')
      .replace(/[^a-z0-9_]/g, ''),
  )

  const rows: Record<string, string>[] = []
  for (let i = 1; i < lines.length; i++) {
    const values = parseLine(lines[i])
    const row: Record<string, string> = {}
    headers.forEach((header, idx) => {
      row[header] = values[idx] || ''
    })
    rows.push(row)
  }

  return rows
}

// ─── Validation ──────────────────────────────────────────────

const REQUIRED_FIELDS = ['category_name', 'item_name', 'price', 'station_code']

export function validateCsv(
  rawRows: Record<string, string>[],
): ValidationResult {
  const validRows: CsvRow[] = []
  const invalidRows: InvalidRow[] = []

  rawRows.forEach((raw, index) => {
    const errors: string[] = []

    // Check required fields
    if (!raw.category_name?.trim()) errors.push('Falta category_name')
    if (!raw.item_name?.trim()) errors.push('Falta item_name')
    if (!raw.station_code?.trim()) errors.push('Falta station_code')

    const price = parseFloat(raw.price || '')
    if (!raw.price?.trim()) {
      errors.push('Falta price')
    } else if (isNaN(price) || price < 0) {
      errors.push('price debe ser un número válido ≥ 0')
    }

    // Validate optional option_price if option_name is present
    let optionPrice: number | undefined
    if (raw.option_name?.trim()) {
      if (raw.option_price?.trim()) {
        optionPrice = parseFloat(raw.option_price)
        if (isNaN(optionPrice)) {
          errors.push('option_price debe ser un número válido')
        }
      } else {
        optionPrice = 0
      }
    }

    if (errors.length > 0) {
      invalidRows.push({ row: index + 2, data: raw, errors }) // +2 for 1-indexed + header
    } else {
      validRows.push({
        category_name: raw.category_name.trim(),
        item_name: raw.item_name.trim(),
        price,
        station_code: raw.station_code.trim(),
        description: raw.description?.trim() || undefined,
        option_name: raw.option_name?.trim() || undefined,
        option_price: optionPrice,
      })
    }
  })

  return { validRows, invalidRows }
}

// ─── Structuring ─────────────────────────────────────────────

export function structureMenu(rows: CsvRow[]): StructuredMenu {
  const categoryMap = new Map<string, Map<string, StructuredItem>>()

  for (const row of rows) {
    if (!categoryMap.has(row.category_name)) {
      categoryMap.set(row.category_name, new Map())
    }
    const itemMap = categoryMap.get(row.category_name)!

    const itemKey = `${row.item_name}__${row.price}__${row.station_code}`
    if (!itemMap.has(itemKey)) {
      itemMap.set(itemKey, {
        name: row.item_name,
        description: row.description || '',
        price: row.price,
        station_code: row.station_code,
        options: [],
      })
    }

    if (row.option_name) {
      const item = itemMap.get(itemKey)!
      // Avoid duplicate options
      if (!item.options.find((o) => o.name === row.option_name)) {
        item.options.push({
          name: row.option_name,
          price: row.option_price ?? 0,
        })
      }
    }
  }

  const categories: StructuredCategory[] = []
  let totalItems = 0
  let totalOptions = 0

  categoryMap.forEach((itemMap, catName) => {
    const items = Array.from(itemMap.values())
    totalItems += items.length
    items.forEach((item) => {
      totalOptions += item.options.length
    })
    categories.push({ name: catName, items })
  })

  return {
    categories,
    totalCategories: categories.length,
    totalItems,
    totalOptions,
  }
}

// ─── Import to Supabase ──────────────────────────────────────

function slugify(text: string): string {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

export async function importMenu(
  restaurantId: string,
  menu: StructuredMenu,
): Promise<ImportResult> {
  let categoriesCreated = 0
  let itemsCreated = 0
  let optionsCreated = 0

  try {
    for (let catIdx = 0; catIdx < menu.categories.length; catIdx++) {
      const cat = menu.categories[catIdx]

      // Create category
      const { data: catData, error: catError } = await supabase
        .from('menu_categories')
        .insert({
          restaurant_id: restaurantId,
          name: cat.name,
          slug: slugify(cat.name),
          sort_order: catIdx,
          is_active: true,
        })
        .select()
        .single()

      if (catError) {
        console.error('[NOIZA][import] Category insert error:', catError)
        throw new Error(
          `Error creando categoría "${cat.name}": ${catError.message}`,
        )
      }
      categoriesCreated++

      // Create items for this category
      for (let itemIdx = 0; itemIdx < cat.items.length; itemIdx++) {
        const item = cat.items[itemIdx]

        const { data: itemData, error: itemError } = await supabase
          .from('menu_items')
          .insert({
            restaurant_id: restaurantId,
            category_id: catData.id,
            name: item.name,
            description: item.description || null,
            price: item.price,
            station_code: item.station_code,
            is_active: true,
            is_customizable: item.options.length > 0,
            sort_order: itemIdx,
          })
          .select()
          .single()

        if (itemError) {
          console.error('[NOIZA][import] Item insert error:', itemError)
          throw new Error(
            `Error creando item "${item.name}": ${itemError.message}`,
          )
        }
        itemsCreated++

        // Create options for this item
        if (item.options.length > 0) {
          const optionsToInsert = item.options.map((opt, optIdx) => ({
            restaurant_id: restaurantId,
            menu_item_id: itemData.id,
            option_group: 'Opciones',
            name: opt.name,
            price_delta: opt.price,
            is_required: false,
            max_select: 1,
            is_active: true,
            sort_order: optIdx,
          }))

          const { error: optError } = await supabase
            .from('menu_item_options')
            .insert(optionsToInsert)

          if (optError) {
            console.error('[NOIZA][import] Options insert error:', optError)
            throw new Error(`Error creando opciones: ${optError.message}`)
          }
          optionsCreated += optionsToInsert.length
        }
      }
    }

    return { success: true, categoriesCreated, itemsCreated, optionsCreated }
  } catch (err: any) {
    return {
      success: false,
      categoriesCreated,
      itemsCreated,
      optionsCreated,
      error: err?.message || 'Error desconocido durante la importación',
    }
  }
}
